import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Auth0AccountRole } from "client/openapi";
import { ReactNode } from "react";

/** What a dragged card carries. */
export type CardDragData = {
  personType: Auth0AccountRole;
  personId: number;
  fromColumnId: number;
  label: string;
};

/** What a column offers as a drop target. */
export type ColumnDropData = {
  columnId: number;
  columnType: Auth0AccountRole;
};

export function cardKey(personType: Auth0AccountRole, personId: number) {
  return `${personType}:${personId}`;
}

/**
 * A person's card. Pressing and moving it a few pixels starts a drag; a plain
 * click still reaches the card's own click handler.
 */
export function DraggableCard({
  id,
  data,
  saving,
  locked,
  children,
}: {
  id: string;
  data: CardDragData;
  saving: boolean;
  locked: boolean;
  children: ReactNode;
}) {
  const { listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data,
    disabled: locked,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      className="visualization-card"
      style={{
        opacity: isDragging || saving ? 0.4 : 1,
        pointerEvents: saving ? "none" : undefined,
      }}
    >
      {children}
    </div>
  );
}

/**
 * A board column. While a card of the same kind is over it, the column is
 * outlined so it is clear where the card will land.
 */
export function DroppableColumn({
  data,
  draggingType,
  draggingFrom,
  className,
  children,
}: {
  data: ColumnDropData;
  draggingType: Auth0AccountRole | null;
  draggingFrom: number | null;
  className: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${data.columnId}`,
    data,
  });

  const isTarget =
    isOver &&
    draggingType === data.columnType &&
    draggingFrom !== data.columnId;

  return (
    <div
      ref={setNodeRef}
      className={`${className}${
        isTarget ? " visualization-column--drop-target" : ""
      }`}
    >
      {children}
    </div>
  );
}
